'use client';

import { memo } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  EntityIcon,
  EntityIconBase,
  HeadingItem,
  MetaDefault,
  type EntityIconType,
} from '@terra/ui-design-system';
import { Key01Icon, Radar01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  GRAPH_NODE_COLOR_PALETTE,
  GRAPH_NODE_ICON,
  type GraphNodeType,
} from '../external-network/constants';
import { AI_ATTACK_PATH_MARKS } from './finding-attack-path-ai-marks';
import type { FindingAttackPathFlowNodeData } from './build-finding-attack-path-flow';

const PRODUCT_ENTITY_ICON: Partial<Record<string, EntityIconType>> = {
  FINDING: 'finding',
  WEAKNESS: 'signal',
  ENDPOINT: 'endpoint',
  WEBPAGE: 'webPage',
};

function isGraphNodeType(entityKind: string): entityKind is GraphNodeType {
  return GRAPH_NODE_ICON[entityKind as GraphNodeType] !== undefined;
}

function AttackPathEntityMark({ entityKind }: { entityKind: string }) {
  const productEntity = PRODUCT_ENTITY_ICON[entityKind];

  if (productEntity) {
    return (
      <EntityIcon
        type={productEntity}
        appearance="highlighted"
        density="large"
      />
    );
  }

  if (entityKind === 'CREDENTIAL') {
    return (
      <EntityIconBase
        icon={Key01Icon}
        appearance="highlighted"
        density="large"
        bg="bg.warning"
        color="fg.warning"
        borderColor="border.warning"
      />
    );
  }

  if (entityKind === 'AI_APPLICATION') {
    return (
      <EntityIcon type="aiEndpoint" appearance="highlighted" density="large" />
    );
  }

  const aiMark = AI_ATTACK_PATH_MARKS[entityKind];

  if (aiMark) {
    return (
      <EntityIconBase
        icon={aiMark.icon}
        appearance="highlighted"
        density="large"
        bg={aiMark.bg}
        color={aiMark.color}
        borderColor={aiMark.borderColor}
      />
    );
  }

  if (entityKind === 'SURFACE') {
    return (
      <EntityIconBase
        icon={Radar01Icon}
        appearance="highlighted"
        density="large"
        bg="bg.entity.entryPoint"
        color="fg.entity.entryPoint"
        borderColor="border.entity.entryPoint"
      />
    );
  }

  if (isGraphNodeType(entityKind)) {
    const palette = GRAPH_NODE_COLOR_PALETTE[entityKind];

    return (
      <EntityIconBase
        icon={GRAPH_NODE_ICON[entityKind]}
        appearance="highlighted"
        density="large"
        bg={`${palette}.3`}
        color={`${palette}.11`}
        borderColor={`${palette}.6`}
      />
    );
  }

  return <EntityIcon type="asset" appearance="highlighted" density="large" />;
}

function FindingAttackPathNodeComponent({
  data,
}: NodeProps<Node<FindingAttackPathFlowNodeData>>) {
  const t = useTranslations('findingAttackPath.kinds');
  const label = data.label || t(data.entityKind);
  const kindLabel = t(data.entityKind);

  return (
    <Box
      bg="bg.default"
      borderWidth="1px"
      borderColor={data.highlighted ? 'border.accent' : 'border.default'}
      borderRadius="lg"
      px="3"
      py="2"
      w="220px"
      overflow="hidden"
      shadow={data.highlighted ? 'sm' : undefined}
      position="relative"
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <HStack gap="2" alignItems="center" w="full" minW={0}>
        <AttackPathEntityMark entityKind={data.entityKind} />
        <VStack
          gap="0"
          alignItems="flex-start"
          minW={0}
          flex="1"
          overflow="hidden"
        >
          <HeadingItem
            title={label}
            w="full"
            minW={0}
            lineClamp={2}
            wordBreak="break-word"
          >
            {label}
          </HeadingItem>
          <MetaDefault
            title={data.sublabel || kindLabel}
            color="fg.muted"
            w="full"
            minW={0}
            truncate
          >
            {data.sublabel || kindLabel}
          </MetaDefault>
        </VStack>
      </HStack>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </Box>
  );
}

export const FindingAttackPathNode = memo(FindingAttackPathNodeComponent);
